import { Container, ContainerProps, Theme } from "@mui/material";
import { SxProps } from "@mui/system";
import { mergeSx } from "merge-sx";

interface InheritedFullPageLayoutProps {
  gridTemplateColumns?:
    | {
        default: ResponsiveCSSSetting;
        fallback: ResponsiveCSSSetting;
      }
    | ResponsiveCSSSetting;

  gridTemplateRows?:
    | {
        default: ResponsiveCSSSetting;
        fallback: ResponsiveCSSSetting;
      }
    | ResponsiveCSSSetting;
}

export type FullPageLayoutProps = InheritedFullPageLayoutProps & ContainerProps;

const FullPageLayout: React.FC<FullPageLayoutProps> = ({
  sx: customSx,
  children,
  gridTemplateColumns,
  gridTemplateRows,
  ...props
}) => {
  // Check if fallback values are provided for grid columns/rows
  const doesGridTemplateColumnSupportFallback =
    typeof gridTemplateColumns === "object" &&
    "default" in gridTemplateColumns &&
    "fallback" in gridTemplateColumns;
  const doesGridTemplateRowSupportFallback =
    typeof gridTemplateRows === "object" &&
    "default" in gridTemplateRows &&
    "fallback" in gridTemplateRows;

  // Use default value if fallback structure is present, else use direct value
  const resolvedColumns = doesGridTemplateColumnSupportFallback
    ? gridTemplateColumns.default
    : gridTemplateColumns;

  const resolvedRows = doesGridTemplateRowSupportFallback
    ? gridTemplateRows.default
    : gridTemplateRows;

  const defaultSx: SxProps<Theme> = {
    height: "calc(100vh - 48px)",
    display: "grid",
    // Fallback grid values for older browsers (placed first)
    ...(doesGridTemplateColumnSupportFallback && {
      gridTemplateColumns: gridTemplateColumns.fallback
    }),
    ...(doesGridTemplateRowSupportFallback && {
      gridTemplateRows: gridTemplateRows.fallback
    }),
    // Advanced grid values for modern browsers (placed last)
    gridTemplateColumns: resolvedColumns,
    gridTemplateRows: resolvedRows,
    alignItems: "stretch",
    justifyItems: "stretch",
    gap: 2,
    padding: 2,
    "& > *": { minWidth: 0 }
  };

  return (
    <Container
      aria-label="Full Page Layout"
      sx={mergeSx(defaultSx, customSx)}
      maxWidth={false}
      disableGutters
      {...props}
    >
      {children}
    </Container>
  );
};

export default FullPageLayout;
